'use client'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Plus, Search, LayoutGrid } from '@/ui/icons'
import { WaypointFolderTree, GpxImporter, AddWaypointToTripPanel } from '@/pages/itinerary-builder/components'
import { WaypointResponse } from '@/shared/api/routes'
import { RoutesApi } from '@/shared/api/routes'
import { useAuth } from '@/modules/auth'
import { useWaypointFolders } from '@/modules/routes/hooks/useWaypointFolders'
import { Button } from '@/ui/components'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/components'
import { useConfirm } from '@/shared/hooks'
import { ConfirmationDialog } from '@/ui/components'
import { cn } from '@/shared/utils'
import type { WaypointFolderTree as WaypointFolderTreeType } from '@/shared/api/waypointFolders'

interface ExplorePanelProps {
  className?: string
  selectedWaypoint?: WaypointResponse | null
  onWaypointClick?: (waypoint: WaypointResponse) => void
  onFolderWaypointsFocus?: (waypoints: WaypointResponse[], folderId: string) => void
  /** Optional map center for new "Quick Pin" waypoints (lat/lng). If not provided, uses 0,0. */
  mapCenter?: { latitude: number; longitude: number } | null
  onWaypointAddedToTrip?: () => void
}

export function ExplorePanel({
  className,
  selectedWaypoint,
  onWaypointClick,
  onFolderWaypointsFocus,
  mapCenter,
  onWaypointAddedToTrip,
}: ExplorePanelProps) {
  const { getAccessToken } = useAuth()
  const [waypoints, setWaypoints] = useState<WaypointResponse[]>([])
  const [loadingWaypoints, setLoadingWaypoints] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [createFolderTrigger, setCreateFolderTrigger] = useState(0)
  const [selectedWaypointForAdd, setSelectedWaypointForAdd] = useState<WaypointResponse | null>(null)
  const [deletingWaypointId, setDeletingWaypointId] = useState<string | null>(null)
  const [creatingWaypoint, setCreatingWaypoint] = useState(false)
  const [treeKey, setTreeKey] = useState(0)
  const { confirm, confirmState, close, setLoading: setConfirmLoading } = useConfirm()

  const routesApi = useMemo(() => new RoutesApi(getAccessToken), [getAccessToken])
  const {
    folders: foldersFromHook,
    moveWaypointToFolder,
    refreshFolders,
  } = useWaypointFolders({ getAccessToken })

  const [optimisticFolders, setOptimisticFolders] = useState<typeof foldersFromHook | null>(null)
  const folders = optimisticFolders ?? foldersFromHook

  useEffect(() => {
    if (!optimisticFolders) return
    setOptimisticFolders(foldersFromHook)
  }, [foldersFromHook])

  const updateFolderInTree = useCallback(
    (
      tree: typeof folders,
      folderId: string,
      updater: (folder: (typeof folders)[0]) => (typeof folders)[0]
    ): typeof folders => {
      return tree.map((folder) => {
        if (folder.id === folderId) return updater(folder)
        if (folder.children?.length) {
          return {
            ...folder,
            children: updateFolderInTree(folder.children, folderId, updater),
          }
        }
        return folder
      })
    },
    []
  )

  const replaceFolderInTree = useCallback(
    (
      tree: typeof folders,
      oldId: string,
      newFolder: (typeof folders)[0]
    ): typeof folders => {
      return tree.map((folder) => {
        if (folder.id === oldId) return newFolder
        if (folder.children?.length) {
          return {
            ...folder,
            children: replaceFolderInTree(folder.children, oldId, newFolder),
          }
        }
        return folder
      })
    },
    []
  )

  const removeFolderFromTree = useCallback(
    (tree: typeof folders, folderId: string): typeof folders => {
      return tree
        .filter((f) => f.id !== folderId)
        .map((folder) => ({
          ...folder,
          children: removeFolderFromTree(folder.children || [], folderId),
        }))
    },
    []
  )

  const addFolderToTree = useCallback(
    (tree: typeof folders, newFolder: (typeof folders)[0], parentId: string | null): typeof folders => {
      if (parentId === null) return [...tree, newFolder]
      return tree.map((folder) => {
        if (folder.id === parentId) {
          return { ...folder, children: [...(folder.children || []), newFolder] }
        }
        if (folder.children?.length) {
          return {
            ...folder,
            children: addFolderToTree(folder.children, newFolder, parentId),
          }
        }
        return folder
      })
    },
    []
  )

  const optimisticCreateFolder = useCallback(
    (newFolder: WaypointFolderTreeType, parentId: string | null) => {
      setOptimisticFolders((prev) => addFolderToTree(prev || foldersFromHook, newFolder as (typeof folders)[0], parentId))
    },
    [foldersFromHook, addFolderToTree]
  )

  const optimisticUpdateFolder = useCallback(
    (folderId: string, updates: Partial<WaypointFolderTreeType>) => {
      setOptimisticFolders((prev) => {
        const current = prev || foldersFromHook
        if (updates.id && updates.id !== folderId) {
          const findFolder = (list: typeof folders, id: string): (typeof folders)[0] | null => {
            for (const f of list) {
              if (f.id === id) return f
              if (f.children?.length) {
                const found = findFolder(f.children, id)
                if (found) return found
              }
            }
            return null
          }
          const oldFolder = findFolder(current, folderId)
          if (oldFolder) {
            const newFolder = { ...oldFolder, ...updates } as (typeof folders)[0]
            return replaceFolderInTree(current, folderId, newFolder)
          }
        }
        return updateFolderInTree(current, folderId, (f) => ({ ...f, ...updates }))
      })
    },
    [foldersFromHook, updateFolderInTree, replaceFolderInTree]
  )

  const optimisticDeleteFolder = useCallback(
    (folderId: string) => {
      setOptimisticFolders((prev) => removeFolderFromTree(prev || foldersFromHook, folderId))
    },
    [foldersFromHook, removeFolderFromTree]
  )

  const fetchWaypoints = useCallback(async (bypassCache = false) => {
    try {
      setLoadingWaypoints(true)
      const all = await routesApi.getWaypoints({}, bypassCache ? { cache: 'no-store' } : undefined)
      setWaypoints(all)
    } catch (err) {
    } finally {
      setLoadingWaypoints(false)
    }
  }, [routesApi])

  useEffect(() => {
    fetchWaypoints()
  }, [fetchWaypoints])

  const handleFolderCreated = useCallback(async () => {
    setCreateFolderTrigger(0)
    await refreshFolders()
    setOptimisticFolders(null)
  }, [refreshFolders])

  const handleWaypointMoved = useCallback(async () => {
    await Promise.all([refreshFolders(), fetchWaypoints()])
    setTreeKey((k) => k + 1)
  }, [refreshFolders, fetchWaypoints])

  const handleWaypointClick = useCallback(
    (waypoint: WaypointResponse) => {
      onWaypointClick?.(waypoint)
    },
    [onWaypointClick]
  )

  const handleFolderClick = useCallback(
    (folderId: string) => {
      const findFolderById = (
        tree: typeof folders,
        targetFolderId: string
      ): (typeof folders)[number] | null => {
        for (const folder of tree) {
          if (folder.id === targetFolderId) return folder
          if (folder.children?.length) {
            const nested = findFolderById(folder.children, targetFolderId)
            if (nested) return nested
          }
        }
        return null
      }

      const collectFolderIds = (folder: (typeof folders)[number]): Set<string> => {
        const folderIds = new Set<string>([folder.id])
        folder.children?.forEach((child) => {
          collectFolderIds(child).forEach((id) => folderIds.add(id))
        })
        return folderIds
      }

      const selectedFolder = findFolderById(folders, folderId)
      if (!selectedFolder) {
        onFolderWaypointsFocus?.([], folderId)
        return
      }

      const targetFolderIds = collectFolderIds(selectedFolder)
      const folderWaypoints = waypoints.filter((waypoint) => {
        if (!waypoint.folder_id) return false
        return targetFolderIds.has(waypoint.folder_id)
      })

      onFolderWaypointsFocus?.(folderWaypoints, folderId)
    },
    [folders, waypoints, onFolderWaypointsFocus]
  )

  const handleWaypointAddToTrip = useCallback((waypoint: WaypointResponse) => {
    setSelectedWaypointForAdd(waypoint)
  }, [])

  const handleWaypointDelete = useCallback(
    async (waypoint: WaypointResponse) => {
      await confirm(
        {
          title: 'Delete Waypoint',
          description: `Are you sure you want to delete "${waypoint.name || 'Unnamed Waypoint'}"? This action cannot be undone.`,
          variant: 'destructive',
          confirmText: 'Delete',
        },
        async () => {
          setDeletingWaypointId(waypoint.id)
          setConfirmLoading(true)
          try {
            await routesApi.deleteWaypoint(waypoint.id)
            await new Promise((r) => setTimeout(r, 200))
            await Promise.all([refreshFolders(), fetchWaypoints(true)])
            setTreeKey((k) => k + 1)
          } catch (e) {
            throw e
          } finally {
            setDeletingWaypointId(null)
            setConfirmLoading(false)
          }
        }
      )
    },
    [confirm, routesApi, refreshFolders, fetchWaypoints, setConfirmLoading]
  )

  const handleCreateQuickPin = useCallback(async () => {
    setCreatingWaypoint(true)
    try {
      const lat = mapCenter?.latitude ?? 0
      const lng = mapCenter?.longitude ?? 0
      await routesApi.createWaypoint({
        name: 'Quick Pin',
        latitude: lat,
        longitude: lng,
        folder_id: null,
        source_type: 'manual',
      })
      await fetchWaypoints(true)
      await refreshFolders()
      setTreeKey((k) => k + 1)
    } catch (e) {
    } finally {
      setCreatingWaypoint(false)
    }
  }, [routesApi, mapCenter, fetchWaypoints, refreshFolders])

  const handleImportComplete = useCallback(async () => {
    await new Promise((r) => setTimeout(r, 300))
    await Promise.all([refreshFolders(), fetchWaypoints(true)])
    setTreeKey((k) => k + 1)
  }, [refreshFolders, fetchWaypoints])

  const libraryWaypoints = useMemo(
    () => waypoints.filter((wp) => wp.folder_id != null),
    [waypoints]
  )

  const rootWaypoints = useMemo(
    () => waypoints.filter((wp) => wp.folder_id == null),
    [waypoints]
  )

  const filteredLibraryWaypoints = useMemo(() => {
    if (!searchQuery.trim()) return libraryWaypoints
    const q = searchQuery.toLowerCase().trim()
    return libraryWaypoints.filter(
      (wp) => wp.name?.toLowerCase().includes(q)
    )
  }, [libraryWaypoints, searchQuery])

  const filteredRootWaypoints = useMemo(() => {
    if (!searchQuery.trim()) return rootWaypoints
    const q = searchQuery.toLowerCase().trim()
    return rootWaypoints.filter((wp) => wp.name?.toLowerCase().includes(q))
  }, [rootWaypoints, searchQuery])

  const waypointsForTree = useMemo(
    () => [...filteredRootWaypoints, ...filteredLibraryWaypoints],
    [filteredRootWaypoints, filteredLibraryWaypoints]
  )

  const totalWaypointCount = waypoints.length
  const totalFolderCount = useMemo(() => {
    const count = (list: typeof folders): number =>
      list.length + list.reduce((acc, f) => acc + count(f.children || []), 0)
    return count(folders)
  }, [folders])

  return (
    <div className={cn('flex flex-col h-full min-h-0', className)}>
      {/* Header actions */}
      <div className="flex items-center gap-2 p-2 border-b border-earth-200 dark:border-earth-800 shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="New folder or waypoint">
              <Plus className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => setCreateFolderTrigger((c) => c + 1)}>
              New folder
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleCreateQuickPin} disabled={creatingWaypoint}>
              {creatingWaypoint ? 'Creating…' : 'New waypoint (Quick Pin)'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="View options">
          <LayoutGrid className="h-4 w-4" />
        </Button>
      </div>

      {/* Search */}
      <div className="p-2 border-b border-earth-200 dark:border-earth-800 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search waypoints..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 rounded-md border border-earth-200 bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-adventure-500"
          />
        </div>
      </div>

      {/* Tree + import */}
      <div className="flex-1 overflow-y-auto min-h-0 p-2 space-y-3">
        <GpxImporter onImportComplete={handleImportComplete} />
        {loadingWaypoints ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Loading waypoints…
          </div>
        ) : (
          <WaypointFolderTree
            key={treeKey}
            waypoints={waypointsForTree}
            folders={folders}
            selectedWaypoint={selectedWaypoint}
            onWaypointClick={handleWaypointClick}
            onFolderClick={handleFolderClick}
            onWaypointMoved={handleWaypointMoved}
            onWaypointAddToTrip={handleWaypointAddToTrip}
            onWaypointDelete={handleWaypointDelete}
            deletingWaypointId={deletingWaypointId}
            onCreateFolderClick={createFolderTrigger > 0 ? createFolderTrigger : undefined}
            onFolderCreated={handleFolderCreated}
            onOptimisticCreateFolder={optimisticCreateFolder}
            onOptimisticUpdateFolder={optimisticUpdateFolder}
            onOptimisticDeleteFolder={optimisticDeleteFolder}
            showRootWaypoints
          />
        )}
      </div>

      {/* Status bar */}
      <div className="shrink-0 px-3 py-2 border-t border-earth-200 dark:border-earth-800 text-muted-foreground text-xs">
        {totalWaypointCount} waypoint{totalWaypointCount !== 1 ? 's' : ''} · {totalFolderCount} folder
        {totalFolderCount !== 1 ? 's' : ''}
      </div>

      {selectedWaypointForAdd && (
        <AddWaypointToTripPanel
          waypoint={selectedWaypointForAdd}
          onClose={() => setSelectedWaypointForAdd(null)}
          onSuccess={async () => {
            await fetchWaypoints(true)
            onWaypointAddedToTrip?.()
            setSelectedWaypointForAdd(null)
          }}
        />
      )}

      <ConfirmationDialog
        open={confirmState?.open ?? false}
        onOpenChange={(open) => !open && close()}
        title={confirmState?.title}
        description={confirmState?.description ?? ''}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        variant={confirmState?.variant}
        onConfirm={confirmState?.onConfirm ?? (() => {})}
        isLoading={confirmState?.isLoading}
      />
    </div>
  )
}
